// ============================================================================
// client/terrain-papier.ts — LES 38 TUILES EN PAPIER DÉCOUPÉ
// ----------------------------------------------------------------------------
// Lot 3 de la refonte (direction D, variante 4). Portage du dessin de tuiles mis
// au point dans la maquette /pages/refonte-d-perso.html, paramétré par BIOME :
// la maquette n'en montrait qu'un (le désert), le jeu en a huit.
//
// La règle qui gouverne la planche, et qu'il ne faut pas défaire : LA FAMILLE
// D'UNE TUILE SE LIT AVANT SA COULEUR.
//   · pleine + ombre portée  = SOLIDE, on marche dessus
//   · ajourée, sans ombre    = FRANCHISSABLE (échelle, corde, sens unique)
//   · frange DENTELÉE        = MORTELLE (lave, feu, pics, zone mortelle)
// C'est ce qui permet de distinguer une fausse brique d'une vraie, ou l'eau de
// la lave, sans avoir à les apprendre.
//
// ⚠️ POURQUOI ON PEUT SE PERMETTRE CE COÛT. Le terrain est peint dans un canvas
// hors écran, repeint SEULEMENT quand sa signature change (client/terrain-cache).
// Une tuile n'est donc pas dessinée soixante fois par seconde mais une fois par
// creusage. Sans ce cache, le papier découpé serait hors de question ici.
// ============================================================================

import type { WorldKey } from '../shared/worlds'
import { bruit, decoupe, dents, frange, getDetail, papier, rect, rgba, setDetail } from './papier'

type Ctx = CanvasRenderingContext2D

const ENCRE = '#3a2c1e'
/* le ton de l'ombre portée et la teinte du papier nu, repris de la matière */
const OMBRE_TON = '#2e2214'
const PAP = '#f2e4cc' 

/** Les couleurs de papier d'un biome. Seules celles-ci changent d'un monde à
 *  l'autre : le DESSIN, lui, est le même partout — c'est ce qui garantit qu'une
 *  échelle reste une échelle et qu'un piège reste reconnaissable. */
export interface PaletteBiome {
  brique: string; briqueHaut: string
  dur: string; durHaut: string
  sable: string; sableHaut: string
  glace: string; glaceHaut: string
  effrite: string; effriteHaut: string
  lave: string; laveHaut: string
  eau: string; eauHaut: string
}

/* Les huit mondes. Les teintes reprennent celles que chaque biome portait déjà
   (shared/worlds.ts et les palettes de gen-sprites), transposées en papier :
   plus franches, moins de tons, un éclairci pour la frange de découpe. */
export const BIOMES: Record<WorldKey, PaletteBiome> = {
  classique: { brique: '#c86a3c', briqueHaut: '#e58a52', dur: '#8f9aa8', durHaut: '#aab4c0',
    sable: '#e8b968', sableHaut: '#f5d089', glace: '#bfe8f5', glaceHaut: '#ffffff',
    effrite: '#c99a5c', effriteHaut: '#e0b478', lave: '#e8511e', laveHaut: '#ff9a2e',
    eau: '#3f95c8', eauHaut: '#79c4e8' },
  grotte: { brique: '#8a6a4a', briqueHaut: '#a8865e', dur: '#6f6f7c', durHaut: '#8a8a98',
    sable: '#c8a878', sableHaut: '#e0c496', glace: '#bfe8f5', glaceHaut: '#ffffff',
    effrite: '#9a7a52', effriteHaut: '#b8966c', lave: '#e8511e', laveHaut: '#ff9a2e',
    eau: '#3a7fa8', eauHaut: '#6fb0d8' },
  prairie: { brique: '#b8703f', briqueHaut: '#d89055', dur: '#a8a48e', durHaut: '#c4c0a8',
    sable: '#e0c078', sableHaut: '#f0d89a', glace: '#cfeaf5', glaceHaut: '#ffffff',
    effrite: '#c09060', effriteHaut: '#d8ac7c', lave: '#e8511e', laveHaut: '#ff9a2e',
    eau: '#4a9ed0', eauHaut: '#84cbe8' },
  desert: { brique: '#cf8a4a', briqueHaut: '#eaaa6a', dur: '#c0a880', durHaut: '#dcc49c',
    sable: '#e8b968', sableHaut: '#f5d089', glace: '#cfe8f0', glaceHaut: '#ffffff',
    effrite: '#d0a068', effriteHaut: '#e8bc88', lave: '#e8511e', laveHaut: '#ff9a2e',
    eau: '#4a9ed0', eauHaut: '#84cbe8' },
  neige: { brique: '#8fa2b4', briqueHaut: '#b0c2d2', dur: '#9aacba', durHaut: '#bcccd8',
    sable: '#d8e4ec', sableHaut: '#f0f6fa', glace: '#d8f0fa', glaceHaut: '#ffffff',
    effrite: '#a8b8c6', effriteHaut: '#c8d6e0', lave: '#e8511e', laveHaut: '#ff9a2e',
    eau: '#5aa8d8', eauHaut: '#96d4f0' },
  usine: { brique: '#5a6472', briqueHaut: '#7a8494', dur: '#4a5260', durHaut: '#68707e',
    sable: '#a89a7a', sableHaut: '#c4b898', glace: '#bfe8f5', glaceHaut: '#ffffff',
    effrite: '#6a7280', effriteHaut: '#8a92a0', lave: '#e8511e', laveHaut: '#ff9a2e',
    eau: '#3a7fa8', eauHaut: '#6fb0d8' },
  abysses: { brique: '#3f8a7e', briqueHaut: '#5faa98', dur: '#456a76', durHaut: '#628a96',
    sable: '#a8bcb0', sableHaut: '#c8dccc', glace: '#bfe8f5', glaceHaut: '#ffffff',
    effrite: '#4f9a8a', effriteHaut: '#6fbaa6', lave: '#e8511e', laveHaut: '#ff9a2e',
    eau: '#2f7fa8', eauHaut: '#5fb0d0' },
  manoir: { brique: '#6a5074', briqueHaut: '#8a6e94', dur: '#5a4a44', durHaut: '#7a665c',
    sable: '#b8a08c', sableHaut: '#d4bca6', glace: '#cfe0ea', glaceHaut: '#ffffff',
    effrite: '#7a6084', effriteHaut: '#9a80a4', lave: '#e8511e', laveHaut: '#ff9a2e',
    eau: '#3f6a9a', eauHaut: '#6f9ac8' },
}

/**
 * `immerge` = la case AU-DESSUS est le même liquide, donc celle-ci n'est pas la
 * surface. Sans cette information, `liquide()` posait sa frange claire sur
 * CHAQUE case : un bassin de six rangées montrait six lignes d'horizon, et la
 * grille se lisait avant l'eau. Le voisinage n'est connu que de l'appelant
 * (paintTerrainStatic parcourt le tableau), d'où le paramètre.
 */
export function tuilePapier(g: Ctx, x: number, y: number, S: number, kind: string, P: PaletteBiome, immerge = false): void {
  const plein = (col: string, motif: string, hautCol?: string): void => { rect(g, x, y, S, S, col, motif); if (hautCol) frange(g, x, y, S, hautCol) }
  /* L'eau est le seul papier SANS grain de fibre : sur un aplat bleu les carrés
     de grain ne se lisent plus comme de la matière mais comme du bruit, et un
     grand bassin en est couvert. Son mouvement lui vient de drawWaterAnim. */
  const liquide = (col: string, motif: string, hautCol: string): void => {
    const d = getDetail()          // rendre le réglage de l'appelant, pas en imposer un
    setDetail(0); papier(g, x, y, S, S, col, motif, OMBRE_TON); setDetail(d)
    if (!immerge) frange(g, x, y + 1, S, hautCol, 1)
  }
  const mortel = (col: string, motif: string, dentCol: string): void => { papier(g, x, y, S, S, col, motif, OMBRE_TON); if (!immerge) dents(g, x, y + 1, S, dentCol) }
  const barre = (col: string): void => { g.fillStyle = col; g.fillRect(x, y + S - 10, S, 10) }
  const fleche = (dx: number, dy: number, col: string): void => { g.fillStyle = col; g.save(); g.translate(x + S / 2, y + S / 2); g.rotate(Math.atan2(dy, dx))
    g.beginPath(); g.moveTo(-9, -7); g.lineTo(9, 0); g.lineTo(-9, 7); g.closePath(); g.fill(); g.restore() }
  switch (kind) {
    case 'Empty': g.strokeStyle = rgba(ENCRE, .16); g.lineWidth = 1; g.setLineDash([4, 4])
      g.strokeRect(x + 3, y + 3, S - 6, S - 6); g.setLineDash([]); break
    case 'Brick': plein(P.brique, 'briques', P.briqueHaut); break
    case 'Concrete': plein(P.dur, 'quadrille', P.durHaut); break
    /* ⚠️ La fausse brique est STRICTEMENT une brique. C'est un piège : le
       distinguo appartient à l'éditeur (marqueur « x »), jamais au jeu. La
       première version de cette planche lui donnait une déchirure visible au
       nom de « la famille se lit avant la couleur » — cette règle vaut pour ce
       qui aide le joueur à décider, pas pour ce qui doit le surprendre. Elle
       révélait tous les pièges de toutes les cartes. Ne pas la remettre. */
    case 'FalseBrick': plein(P.brique, 'briques', P.briqueHaut); break
    case 'Sand': plein(P.sable, 'pois', P.sableHaut); break
    case 'Ice': plein(P.glace, 'chevrons', P.glaceHaut); break
    case 'Crumble': plein(P.effrite, 'briques', P.effriteHaut)          // le sol qui s'effrite : la découpe part en morceaux
      g.fillStyle = PAP
      for (let k = 0; k < 5; k++) { const bx = x + 5 + ((bruit(x + k, y) * (S - 14)) | 0), by = y + 8 + ((bruit(y, x + k) * (S - 18)) | 0)
        g.beginPath(); g.arc(bx, by, 3.4, 0, 7); g.fill() }
      break
    /* LE TROU CREUSÉ, ses deux états. Ce sont les seules « tuiles » que le
       terrain ne connaît pas : le moteur les tient à part (cur.holes), et elles
       se dessinent PAR-DESSUS le cache. En papier, un trou n'est pas un carré
       noir — c'est la brique dont on a arraché le milieu : il reste des
       lambeaux accrochés aux bords, et le fond de la page apparaît. */
    case 'Hole': {
      g.fillStyle = rgba(OMBRE_TON, .62); g.fillRect(x, y, S, S)
      g.fillStyle = P.brique                       // les lambeaux encore accrochés
      for (const [bx, bw] of [[0, 11], [S - 9, 9]] as [number, number][]) {
        g.beginPath(); g.moveTo(x + bx, y); g.lineTo(x + bx + bw, y)
        g.lineTo(x + bx + bw * .5, y + 14 + bruit(x + bx, y) * 9); g.lineTo(x + bx, y + 8); g.closePath(); g.fill()
      }
      dents(g, x, y + 3, S, P.brique, 5)           // le bord supérieur, déchiré
      g.fillStyle = rgba(OMBRE_TON, .3); g.fillRect(x, y + S - 5, S, 5)
      break
    }
    case 'HoleRegrow': {                           // le papier se recolle : la brique revient
      plein(P.brique, 'briques', P.briqueHaut)
      g.fillStyle = rgba(OMBRE_TON, .34)
      g.beginPath(); g.moveTo(x + 8, y); g.lineTo(x + S - 8, y)
      g.lineTo(x + S - 13, y + 15); g.lineTo(x + 13, y + 13); g.closePath(); g.fill()
      break
    }
    case 'Lava': mortel(P.lave, 'marbre', P.laveHaut); break
    case 'Water': liquide(P.eau, 'eau', P.eauHaut); break
    case 'Bubbles': liquide(P.eau, 'eau', P.eauHaut)
      g.fillStyle = rgba('#ffffff', .75)
      for (let k = 0; k < 5; k++) { g.beginPath(); g.arc(x + 10 + k * 8, y + S - 8 - ((k * 13) % 26), 3 - k * .3, 0, 7); g.fill() }
      break
    case 'Fire': mortel('#ff7a1e', 'marbre', '#ffd24a'); break
    case 'Spikes': g.fillStyle = rgba(OMBRE_TON, .2); g.fillRect(x + 3, y + S - 8, S - 6, 6)
      dents(g, x, y + S - 4, S, '#e8e0cc', 22); break
    case 'DeathZone': papier(g, x, y, S, S, '#b054d0', 'chevrons', OMBRE_TON)
      dents(g, x, y + 1, S, '#e0a0f0'); dents(g, x, y + S, S, '#e0a0f0', -6); break
    case 'Ladder': for (const dx of [7, S - 13]) rect(g, x + dx, y, 6, S, '#a06a34', 'plis')
      for (let k = 0; k < 2; k++) rect(g, x + 7, y + 12 + k * 20, S - 20, 6, '#cf9048', 'plis'); break
    case 'ExitLadder': for (const dx of [7, S - 13]) rect(g, x + dx, y, 6, S, '#e0a020', 'guilloche')
      for (let k = 0; k < 2; k++) rect(g, x + 7, y + 12 + k * 20, S - 20, 6, '#ffd23f', 'plis'); break
    /* l'ombre de la corde s'arrête au bord de la case : elle débordait de 3 px, ce
       qui laissait un moignon dans le vide au bout de toute corde. */
    case 'Rope': g.strokeStyle = rgba(OMBRE_TON, .26); g.lineWidth = 5
      g.beginPath(); g.moveTo(x, y + 13); g.lineTo(x + S, y + 13); g.stroke()
      g.strokeStyle = '#a06a34'; g.lineWidth = 4; g.beginPath(); g.moveTo(x, y + 9); g.lineTo(x + S, y + 9); g.stroke(); break
    /* les trois PAIRES de téléporteurs : même dessin, trois teintes. Elles ne
       sont pas décoratives — c'est ce qui dit où l'on va ressortir. */
    case 'Teleporter': case 'Teleporter0': case 'Teleporter1': case 'Teleporter2': {
      const TP = ['#2fb0c8', '#8b5cf6', '#e8a020']
      const col = TP[Number(kind.slice(-1)) || 0] ?? TP[0]!
      const r = S * 0.34
      decoupe(g, () => { g.beginPath(); g.arc(x + S / 2, y + S / 2, r, 0, 7) }, col, 'guilloche', { x, y, w: S, h: S })
      g.strokeStyle = rgba('#ffffff', .85); g.lineWidth = Math.max(1.4, S / 22); g.beginPath()
      for (let a = 0; a < 17; a += .2) g.lineTo(x + S / 2 + Math.cos(a) * a * (r / 18), y + S / 2 + Math.sin(a) * a * (r / 18))
      g.stroke(); break
    }
    case 'ConveyorL': plein(P.dur, 'quadrille', P.durHaut); fleche(-1, 0, rgba(ENCRE, .5)); break
    case 'ConveyorR': plein(P.dur, 'quadrille', P.durHaut); fleche(1, 0, rgba(ENCRE, .5)); break
    case 'ConveyorUp': plein(P.dur, 'quadrille', P.durHaut); fleche(0, -1, rgba(ENCRE, .5)); break
    case 'ConveyorDown': plein(P.dur, 'quadrille', P.durHaut); fleche(0, 1, rgba(ENCRE, .5)); break
    case 'Trampoline': barre(rgba(OMBRE_TON, .25)); rect(g, x, y + S - 13, S, 13, '#3fbc80', 'plis'); frange(g, x, y + S - 13, S, '#6fe0a8'); break
    case 'TrampolineStrong': rect(g, x, y + S - 17, S, 17, '#2f9a68', 'chevrons'); frange(g, x, y + S - 17, S, '#7fefb8')
      g.fillStyle = '#e8fff4'; for (const dx of [14, S - 14]) { g.beginPath()
        g.moveTo(x + dx, y + S - 26); g.lineTo(x + dx - 5, y + S - 19); g.lineTo(x + dx + 5, y + S - 19); g.fill() } break
    case 'TrampolineL': rect(g, x, y + S - 13, S, 13, '#3fbc80', 'plis'); fleche(-1, -1, '#e8fff4'); break
    case 'TrampolineR': rect(g, x, y + S - 13, S, 13, '#3fbc80', 'plis'); fleche(1, -1, '#e8fff4'); break
    case 'TrampolineStrongL': rect(g, x, y + S - 17, S, 17, '#2f9a68', 'chevrons'); fleche(-1, -1.6, '#e8fff4'); break
    case 'TrampolineStrongR': rect(g, x, y + S - 17, S, 17, '#2f9a68', 'chevrons'); fleche(1, -1.6, '#e8fff4'); break
    case 'RampUpRight': decoupe(g, () => { g.beginPath(); g.moveTo(x, y + S); g.lineTo(x + S, y + 6); g.lineTo(x + S, y + S); g.closePath() }, P.brique, 'briques', { x, y, w: S, h: S }); break
    case 'RampUpLeft': decoupe(g, () => { g.beginPath(); g.moveTo(x, y + 6); g.lineTo(x + S, y + S); g.lineTo(x, y + S); g.closePath() }, P.brique, 'briques', { x, y, w: S, h: S }); break
    case 'DoorClosed': rect(g, x + 4, y, S - 8, S, '#a06a34', 'plis')
      g.fillStyle = '#ffcf1f'; g.beginPath(); g.arc(x + S - 13, y + S / 2, 4, 0, 7); g.fill(); break
    case 'Barrier': for (let k = 0; k < 3; k++) rect(g, x + 6 + k * 15, y, 8, S, '#8f9aa8', 'quadrille'); break
    case 'BarrierOpen': g.globalAlpha = .3; for (let k = 0; k < 3; k++) rect(g, x + 6 + k * 15, y, 8, S, '#8f9aa8', 'quadrille', false); g.globalAlpha = 1; break
    case 'Button': rect(g, x + 8, y + S - 12, S - 16, 12, '#c04a4a', 'plis'); frange(g, x + 8, y + S - 12, S - 16, '#e87070'); break
    case 'OneWayR': g.strokeStyle = rgba('#3fbc80', .8); g.lineWidth = 3; g.setLineDash([6, 5])
      g.beginPath(); g.moveTo(x + 2, y); g.lineTo(x + 2, y + S); g.stroke(); g.setLineDash([]); fleche(1, 0, rgba('#3fbc80', .8)); break
    case 'OneWayL': g.strokeStyle = rgba('#3fbc80', .8); g.lineWidth = 3; g.setLineDash([6, 5])
      g.beginPath(); g.moveTo(x + S - 2, y); g.lineTo(x + S - 2, y + S); g.stroke(); g.setLineDash([]); fleche(-1, 0, rgba('#3fbc80', .8)); break
    case 'DashCrystal': decoupe(g, () => { g.beginPath()
      g.moveTo(x + S / 2, y + 8); g.lineTo(x + S - 12, y + S / 2); g.lineTo(x + S / 2, y + S - 8); g.lineTo(x + 12, y + S / 2); g.closePath() }, '#6afcff', 'guilloche', { x, y, w: S, h: S })
      g.fillStyle = rgba('#ffffff', .8); g.beginPath(); g.arc(x + S / 2 - 4, y + S / 2 - 5, 3.4, 0, 7); g.fill(); break
    case 'BombRefill': rect(g, x + 8, y + 12, S - 16, S - 20, '#5a4a3a', 'plis')
      g.fillStyle = '#2e2438'; g.beginPath(); g.arc(x + S / 2, y + S - 18, 9, 0, 7); g.fill()
      g.strokeStyle = '#e0a020'; g.lineWidth = 2.4; g.beginPath(); g.moveTo(x + S / 2 + 5, y + S - 26)
      g.quadraticCurveTo(x + S / 2 + 12, y + S - 34, x + S / 2 + 6, y + S - 38); g.stroke(); break
    case 'Checkpoint': decoupe(g, () => { g.beginPath(); g.ellipse(x + S / 2, y + S - 16, 20, 9, 0, 0, 7) }, '#e08a70', 'pois', { x, y, w: S, h: S })
      g.fillStyle = ENCRE; g.beginPath(); g.arc(x + S / 2 + 12, y + S - 18, 1.7, 0, 7); g.fill(); break
    default: g.strokeStyle = rgba(ENCRE, .2); g.strokeRect(x + 3, y + 3, S - 6, S - 6)
  }
}

